import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { io, type Socket } from 'socket.io-client'
import { useAuth, useWorkspace } from '@/context'
import { reportAuthLogout } from '@/services/authLogoutReport'
import { isUserActionLogoutReason } from '@/services/authTypes'
import { refreshAccessToken } from '@/services/tokenRefresh'
import { getPresenceDisplayName } from '@/utils/userProfile'

function isSocketAuthError(message: string): boolean {
	const lower = message.toLowerCase()
	return (
		lower.includes('auth') ||
		lower.includes('401') ||
		lower.includes('unauthorized') ||
		lower.includes('token has expired')
	)
}

interface SocketContextType {
	socket: Socket | null
	connected: boolean
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

interface SocketProviderProps {
	children: ReactNode
}

export function SocketProvider({ children }: SocketProviderProps) {
	const { user, triggerLogoutOverlay } = useAuth()
	const { selectedWorkspaceId } = useWorkspace()
	const [socket, setSocket] = useState<Socket | null>(null)
	const [connected, setConnected] = useState(false)
	const currentWorkspaceIdRef = useRef<string | null>(null)
	const socketRef = useRef<Socket | null>(null)
	const activityIntervalRef = useRef<NodeJS.Timeout | null>(null)

	// Determine WebSocket URL and path (same pattern as API_BASE_URL)
	const getWebSocketConfig = useCallback((): { url: string; path: string } => {
		const runtimeWsUrl = window.RUNTIME_CONFIG?.WS_BASE_URL
		const viteWsUrl = import.meta.env.VITE_WS_URL

		if (runtimeWsUrl) {
			// Production: use full URL from runtime config
			// Backend mounts Socket.IO at /ws, so path is /ws/socket.io
			const baseUrl = runtimeWsUrl.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:')
			return { url: baseUrl, path: '/ws/socket.io' }
		}

		if (viteWsUrl) {
			// Development with custom WS URL: use it directly
			const baseUrl = viteWsUrl.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:')
			return { url: baseUrl, path: '/ws/socket.io' }
		}

		// Development: use relative URL to leverage Vite proxy
		// Vite proxies /ws to the backend, Socket.IO is at /ws/socket.io
		return { url: '', path: '/ws/socket.io' }
	}, [])

	// Keep socketRef in sync with socket state
	useEffect(() => {
		socketRef.current = socket
	}, [socket])

	useEffect(() => {
		const handleTokensRefreshed = () => {
			const currentSocket = socketRef.current
			if (!currentSocket) {
				return
			}
			if (!currentSocket.connected) {
				currentSocket.connect()
			}
		}

		window.addEventListener('tokens-refreshed', handleTokensRefreshed)
		return () => window.removeEventListener('tokens-refreshed', handleTokensRefreshed)
	}, [])

	useEffect(() => {
		// Only connect if user is authenticated
		if (!user?.id) {
			// Clear activity interval if exists
			if (activityIntervalRef.current) {
				clearInterval(activityIntervalRef.current)
				activityIntervalRef.current = null
			}
			// Disconnect if socket exists
			const currentSocket = socketRef.current
			if (currentSocket) {
				currentSocket.disconnect()
				setSocket(null)
				setConnected(false)
			}
			return
		}

		const { url: socketUrl, path: socketPath } = getWebSocketConfig()

		const socketOptions: Parameters<typeof io>[1] = {
			path: socketPath,
			transports: ['websocket', 'polling'],
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionAttempts: 5,
			withCredentials: true,
		}

		// Create socket connection
		const newSocket = io(socketUrl || undefined, socketOptions)

		newSocket.on('connect', () => {
			setConnected(true)
			// Subscribe to user room on connect
			if (user.id) {
				newSocket.emit('subscribe_user', { user_id: user.id })
			}

			// Start periodic activity ping (every 30 seconds)
			// This updates session last_active_at for audit/display purposes
			activityIntervalRef.current = setInterval(() => {
				if (newSocket.connected) {
					newSocket.emit('activity', { timestamp: Date.now() })
				}
			}, 30 * 1000) // 30 seconds
		})

		newSocket.on('disconnect', () => {
			setConnected(false)
		})

		newSocket.on('connect_error', async (error: Error) => {
			console.error('Socket connection error:', error)
			setConnected(false)
			if (!isSocketAuthError(error.message)) {
				return
			}

			const refreshResult = await refreshAccessToken()
			if (refreshResult.ok) {
				newSocket.connect()
				return
			}

			const logoutReason = isUserActionLogoutReason(refreshResult.logoutReason)
				? refreshResult.logoutReason
				: 'session_expired'

			reportAuthLogout(logoutReason, 'websocket_connect_error', {
				message: error.message,
			})
			triggerLogoutOverlay(logoutReason)
		})

		newSocket.on('subscribed', (data: { room?: string; status?: string }) => {
			console.debug('Subscribed to room:', data)
		})

		setSocket(newSocket)
		socketRef.current = newSocket
		currentWorkspaceIdRef.current = null

		// Cleanup on unmount or when user changes
		return () => {
			if (activityIntervalRef.current) {
				clearInterval(activityIntervalRef.current)
				activityIntervalRef.current = null
			}
			newSocket.disconnect()
			setSocket(null)
			setConnected(false)
			socketRef.current = null
		}
	}, [user?.id, triggerLogoutOverlay, getWebSocketConfig])

	// Handle workspace subscription changes
	useEffect(() => {
		if (!socket || !connected || !selectedWorkspaceId) {
			// Unsubscribe from previous workspace if exists
			if (socket && connected && currentWorkspaceIdRef.current) {
				socket.emit('unsubscribe_workspace', { workspace_id: currentWorkspaceIdRef.current })
				currentWorkspaceIdRef.current = null
			}
			return
		}

		// If workspace changed, unsubscribe from old one
		if (currentWorkspaceIdRef.current && currentWorkspaceIdRef.current !== selectedWorkspaceId) {
			socket.emit('unsubscribe_workspace', { workspace_id: currentWorkspaceIdRef.current })
		}

		// Subscribe to new workspace
		if (currentWorkspaceIdRef.current !== selectedWorkspaceId) {
			socket.emit('subscribe_workspace', {
				workspace_id: selectedWorkspaceId,
				display_name: getPresenceDisplayName(user),
			})
			currentWorkspaceIdRef.current = selectedWorkspaceId
		}
	}, [socket, connected, selectedWorkspaceId, user])

	const value = {
		socket,
		connected,
	}

	return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export function useSocket() {
	const context = useContext(SocketContext)
	if (context === undefined) {
		throw new Error('useSocket must be used within a SocketProvider')
	}
	return context
}
